/**
 * The ONE place a public guest URL is built — doc 02 §5.
 *
 * Before this module, 32 call sites across 24 files concatenated `/m/${menuSlug}`
 * by hand: lib/qr-code.ts, lib/server/sitemap-data.ts, lib/server/printing/receipt.ts,
 * app/manifest.webmanifest/route.ts, the AI ordering tools, the customer dashboard,
 * the chow scan target, the maps preview. Moving the public address from a menu slug
 * to `/m/<business>/<branch>` with 45 independent copies of the old shape is how a
 * printed QR code silently stops working.
 *
 * `scripts/check-public-routes.mjs` fails the build on a literal `/m/` anywhere but
 * here, so the next address change is a change to this file.
 *
 * Client-safe: imports only `lib/routing/hosts`, which imports only `lib/site-url`.
 *
 * @location lib/routing/place-href.ts
 */

import { guestCanonicalUrl } from '@/lib/routing/hosts';

/** The public mount for guest pages. The only literal in the codebase. */
const PLACE_MOUNT = '/m';

/**
 * Identifies a place in a URL. `branch` is optional because the business hub is a
 * real destination (the branch chooser, doc 07 §2) — not a redirect stop.
 */
export interface PlaceRef {
  /** `businesses.slug`. Already NOT NULL UNIQUE. */
  business: string | null | undefined;
  /** `branches.public_slug`. Omit for the business hub. */
  branch?: string | null;
}

/**
 * Query parameters a place URL is allowed to carry.
 *
 * Deliberately a closed set. These survive the alias 301 (doc 02 §6.2) — a redirect
 * that drops `table_code` turns a dine-in order into a takeaway, which is the single
 * most expensive way this migration could fail — so the set that must survive has to
 * be written down somewhere the redirect code can read it.
 */
export interface PlaceParams {
  /** The line tab to open (`menu_lines.slug`). Doc 03 §3. */
  line?: string | null;
  /** Set by every printed code, so analytics can tell a scan from a click. */
  qrcode?: boolean;
  /** `branch_tables.public_code`. Resolves the branch on its own (ladder rung 2). */
  table_code?: string | null;
  /** `branch_service_zones` public code. */
  zone_code?: string | null;
  /** Counter-pickup variant of a table code. */
  counter_pickup?: boolean;
  /** Kiosk chrome — a mode, not a route, after doc 02 §5. */
  kiosk?: boolean;
  /** Signed preview token, replacing the `/preview/<slug>` tree. */
  preview?: string | null;
  /**
   * Which menu body the guest asked for: `default` (Menuraq's own) or `design`
   * (the restaurant's template). Part of the address, not a cookie — see
   * `lib/customer/place-menu-view.ts`.
   */
  view?: string | null;
  /** Customer presentation version. Old public routes remain addressable as v1. */
  experience?: 'v1' | 'v2' | null;
  /** Return to the business branch chooser even when this device remembers a branch. */
  choose?: boolean;
}

/**
 * Every query key that MUST survive an alias redirect. Exported so the redirect
 * path and its test read from the same list rather than each keeping a copy.
 */
export const PRESERVED_PLACE_PARAMS = [
  'line',
  'qrcode',
  'table_code',
  'zone_code',
  'counter_pickup',
  'kiosk',
  'preview',
  'view',
  'experience',
] as const;

export type PreservedPlaceParam = (typeof PRESERVED_PLACE_PARAMS)[number];
type PlaceQueryParam = PreservedPlaceParam | 'choose';

const PLACE_QUERY_PARAMS: readonly PlaceQueryParam[] = [
  ...PRESERVED_PLACE_PARAMS,
  'choose',
];

/**
 * `?? ''` is not defensive padding — it is the difference between a degraded link and
 * a white screen on the most important page in the product.
 *
 * This threw for real during the address cutover: `MenuDisplayPage` read the menu slug
 * off a route param that the rename had renamed, passed `undefined` in, and
 * `value.trim()` crashed the whole public menu for every guest who reached it. The
 * caller was wrong and has been fixed; this makes the same class of mistake produce a
 * link that goes nowhere useful instead of a page that renders nothing at all.
 */
function encodeSegment(value: string | null | undefined): string {
  return encodeURIComponent((value ?? '').trim());
}

function buildQuery(params?: PlaceParams): string {
  if (!params) return '';
  const search = new URLSearchParams();

  // Fixed order, not object order: a QR code's payload must be byte-stable across
  // regenerations or the printed image changes for no reason and merchants reprint.
  for (const key of PLACE_QUERY_PARAMS) {
    const value = params[key];
    if (value === undefined || value === null || value === false || value === '') continue;
    search.set(key, value === true ? (key === 'choose' ? '1' : 'true') : String(value));
  }

  const query = search.toString();
  return query ? `?${query}` : '';
}

/**
 * The path part of a place URL, root-relative. Use for `<Link>`, `redirect()` and
 * anything staying inside the app.
 */
export function placePath(ref: PlaceRef, params?: PlaceParams): string {
  const business = encodeSegment(ref.business);
  const branch = ref.branch ? encodeSegment(ref.branch) : null;
  const path = branch ? `${PLACE_MOUNT}/${business}/${branch}` : `${PLACE_MOUNT}/${business}`;
  return `${path}${buildQuery(params)}`;
}

/**
 * The absolute, canonical place URL.
 *
 * Context-free on purpose (the property `guestCanonicalUrl` already guarantees and
 * `lib/qr-code.ts` depends on): a code generated from a preview deployment, a laptop
 * or a server render must encode the same address, and it must never change under a
 * code already stuck to a table.
 */
export function placeUrl(ref: PlaceRef, params?: PlaceParams): string {
  return guestCanonicalUrl(placePath(ref, params));
}

/**
 * A sub-page of a place: `/m/<business>/<branch>/book`, `/reserve`, `/ready`.
 *
 * Doc 02 §5 collapses seven identifier kinds into two — a *place* gets a readable
 * path, a *record* keeps an opaque id. These are the place-scoped ones; order,
 * receipt, invoice and quote pages keep their own trees and are not built here.
 */
export type PlaceSubPage = 'about' | 'book' | 'reserve' | 'ready' | 'confirmed' | 'rewards' | 'gift-cards' | 'orders' | 'activity' | 'account';

/** Sub-pages that belong to the BUSINESS, not to one branch. */
const BUSINESS_SUB_PAGES: ReadonlySet<PlaceSubPage> = new Set(['confirmed', 'rewards', 'gift-cards', 'activity', 'account']);

export function placeSubPath(
  ref: PlaceRef,
  page: PlaceSubPage,
  params?: PlaceParams,
): string {
  const businessScoped = BUSINESS_SUB_PAGES.has(page);
  const base = placePath(businessScoped ? { business: ref.business } : ref);
  return `${base}/${page}${buildQuery(params)}`;
}

export function placeSubUrl(ref: PlaceRef, page: PlaceSubPage, params?: PlaceParams): string {
  return guestCanonicalUrl(placeSubPath(ref, page, params));
}

/** Build a customer account URL while retaining an optional verified-place hint. */
export function placeAccountPath(input: {
  business: string;
  branchId?: string | null;
  branchSlug?: string | null;
  experience?: 'v1' | 'v2';
}): string {
  const query = new URLSearchParams();
  if (input.branchId) query.set('branchId', input.branchId);
  if (input.branchSlug) query.set('branchSlug', input.branchSlug);
  if (input.experience === 'v2') query.set('experience', 'v2');
  const base = placeSubPath({ business: input.business }, 'account');
  return `${base}${query.size ? `?${query.toString()}` : ''}`;
}

/** Business-scoped order record detail; the access token remains the bearer credential. */
export function placeOrderTrackPath(input: {
  business: string;
  orderId: string;
  accessToken: string;
  experience?: 'v1' | 'v2';
}): string {
  const params = new URLSearchParams({ token: input.accessToken });
  if (input.experience) params.set('experience', input.experience);
  return `${placeSubPath({ business: input.business }, 'orders')}/${encodeURIComponent(input.orderId)}?${params.toString()}`;
}

/** V2 order confirmation screen; the bearer remains in the query for the status lookup. */
export function placeOrderConfirmedPath(input: {
  business: string;
  branchSlug?: string | null;
  orderId: string;
  accessToken: string;
}): string {
  const params = new URLSearchParams({ token: input.accessToken, experience: 'v2' });
  if (input.branchSlug) params.set('branch', input.branchSlug);
  return `${placeSubPath({ business: input.business }, 'confirmed')}/${encodeURIComponent(input.orderId)}?${params.toString()}`;
}

/**
 * Carry a request's preserved parameters across an alias 301.
 *
 * The old shape is `/m/<menuSlug>?qrcode=true&table_code=T12`; the new one is
 * `/m/<business>/<branch>?qrcode=true&table_code=T12`. Anything not in
 * `PRESERVED_PLACE_PARAMS` is dropped deliberately — a redirect is a good moment to
 * stop propagating stale tracking junk, but never a good moment to lose the table.
 */
export function carryPlaceParams(search: URLSearchParams): PlaceParams {
  const params: PlaceParams = {};
  for (const key of PRESERVED_PLACE_PARAMS) {
    const raw = search.get(key);
    if (raw === null) continue;
    if (key === 'qrcode' || key === 'counter_pickup' || key === 'kiosk') {
      params[key] = raw !== 'false' && raw !== '0';
    } else if (key === 'experience') {
      if (raw === 'v1' || raw === 'v2') params.experience = raw;
    } else {
      params[key] = raw;
    }
  }
  return params;
}

// ─── The migration seam ──────────────────────────────────────────────────────
//
// The address is `/m/<menuSlug>` today and `/m/<business>/<branch>` after the
// cutover (doc 02). The 32 sites that build a guest URL cannot move to `placePath`
// yet — they hold a menu slug and nothing else, and the business/branch pair only
// becomes available once the alias table and `branches.public_slug` exist.
//
// So they move HERE first, unchanged. These helpers emit exactly today's shape, byte
// for byte. What that buys is the thing the module exists for: when the address
// flips, one file changes instead of 32, and `scripts/check-public-routes.mjs` can be
// ratcheted to zero and added to lint immediately rather than at the very end of the
// migration, which is when a regression is most likely to slip through unnoticed.
//
// Every function below is deliberately marked deprecated. They are not a permanent
// API — they are a list of the call sites still waiting on task 1.6.

/**
 * The place root, addressed by menu slug.
 * @deprecated Migration seam. Becomes `placePath({ business, branch })`.
 */
export function legacyMenuPath(menuSlug: string | null | undefined, params?: PlaceParams): string {
  return `${PLACE_MOUNT}/${encodeSegment(menuSlug)}${buildQuery(params)}`;
}

/**
 * Absolute form, for printed codes and anything crossing a host boundary.
 * @deprecated Migration seam. Becomes `placeUrl({ business, branch })`.
 */
export function legacyMenuUrl(menuSlug: string | null | undefined, params?: PlaceParams): string {
  return guestCanonicalUrl(legacyMenuPath(menuSlug, params));
}

/**
 * The guest's order tracker: `/m/<slug>/orders/<orderId>?token=…`.
 *
 * The token is the sole credential — it is what lets a guest with no account reopen
 * their own order — so it is encoded here rather than trusted to each call site.
 *
 * @deprecated Migration seam. The order id stays opaque (doc 02 §5: a *record* keeps
 * an id); only the place half of the path changes.
 */
export function legacyOrderTrackPath(
  menuSlug: string,
  orderId: string,
  accessToken: string,
): string {
  const search = new URLSearchParams({ token: accessToken });
  return `${PLACE_MOUNT}/${encodeSegment(menuSlug)}/orders/${encodeURIComponent(orderId)}?${search}`;
}

/**
 * Rebuild a past order's cart: `/m/reorder/<slug>?order=…&token=…`.
 * @deprecated Migration seam.
 */
export function legacyReorderPath(
  menuSlug: string,
  order: { orderId: string; accessToken: string },
  experience?: 'v1' | 'v2',
): string {
  const search = new URLSearchParams({ order: order.orderId, token: order.accessToken });
  if (experience) search.set('experience', experience);
  return `${PLACE_MOUNT}/reorder/${encodeSegment(menuSlug)}?${search}`;
}

/**
 * "Order my usual": `/m/usual/<slug>`.
 * @deprecated Migration seam.
 */
export function legacyUsualPath(menuSlug: string): string {
  return `${PLACE_MOUNT}/usual/${encodeSegment(menuSlug)}`;
}

/**
 * Whether a pathname is inside the public guest tree.
 *
 * Three components test this by hand with `startsWith('/m/')` to decide chrome
 * (cookie banner, toast placement, loading skeleton). Those are correct today and
 * will be correct after the cutover too — the mount does not change — but they are
 * the same fact written four times, so they read it from here.
 */
export function isPlacePathname(pathname: string | null | undefined): boolean {
  return Boolean(pathname && (pathname === PLACE_MOUNT || pathname.startsWith(`${PLACE_MOUNT}/`)));
}
